import { useState, useEffect, useRef } from "react";
import { Mail, X, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";

/**
 * Non-blocking banner shown to logged-in users whose `email_verified` flag is
 * false. Lets the user re-send the verification link with one click.
 * Grandfathered users (accounts created before the field existed) have
 * `email_verified === true` after the backfill migration, so they never see
 * this banner.
 */
export default function EmailVerificationBanner() {
  const { user, refresh } = useAuth();
  const [hidden, setHidden] = useState(false);
  const [sending, setSending] = useState(false);

  // Poll once on mount in case the user just verified in another tab.
  const polled = useRef(false);
  useEffect(() => {
    if (!polled.current && user && !user.email_verified) {
      polled.current = true;
      refresh?.();
    }
  }, [user, refresh]);

  if (!user) return null;
  if (user.email_verified) return null;
  if (hidden) return null;

  const resend = async () => {
    setSending(true);
    try {
      await api.post("/auth/send-verification");
      toast.success("Verification email sent — check your inbox.");
    } catch {
      toast.error("Couldn't send the verification email. Try again in a moment.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="relative flex flex-wrap items-center gap-3 px-4 py-3 mx-auto max-w-6xl mt-4 rounded-lg"
      style={{
        background: "var(--bg-cream)",
        border: "1px solid var(--border-stone)",
        color: "var(--text-primary)",
        zIndex: 10001, // above react-joyride overlay (default 100)
      }}
      data-testid="email-verification-banner"
    >
      <Mail className="w-4 h-4 shrink-0" style={{ color: "var(--accent-copper)" }} />
      <div className="text-sm flex-1 min-w-[200px]">
        <strong>Please confirm your email.</strong>{" "}
        <span style={{ color: "var(--text-secondary)" }}>
          Check <strong>{user.email}</strong> for a link. Confirming unlocks password reset and email notifications.
        </span>
      </div>
      <button
        type="button"
        onClick={resend}
        disabled={sending}
        className="btn-primary text-xs flex items-center gap-1"
        data-testid="email-verification-resend-btn"
      >
        {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
        {sending ? "Sending…" : "Resend link"}
      </button>
      <button
        type="button"
        onClick={() => setHidden(true)}
        className="btn-ghost text-xs p-1.5"
        style={{ border: "1px solid var(--border-stone)" }}
        data-testid="email-verification-dismiss-btn"
        aria-label="Dismiss verification banner"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
